/**
 * F606BroadcastAdmin — compose an announcement, dispatch to an audience,
 * and poll progress via /commands/{id}.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Megaphone, Send } from 'lucide-react';
import { bridge } from './bridgeClient';

const AUDIENCES = [
  { value: 'all',           ar: 'الجميع',              en: 'All users' },
  { value: 'active_users',  ar: 'المستخدمون النشطون', en: 'Active users' },
  { value: 'premium',       ar: 'مشتركو Premium',    en: 'Premium subscribers' },
  { value: 'free',          ar: 'مستخدمو Free',       en: 'Free users' },
  { value: 'trainers',      ar: 'المدرّبون',           en: 'Trainers' },
  { value: 'trainees',      ar: 'المتدرّبون',          en: 'Trainees' },
];

export default function F606BroadcastAdmin({ language }) {
  const isAr = language === 'ar';
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState('all');
  const [channels, setChannels] = useState({ push: true, email: true, in_app: true });
  const [sending, setSending] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(null);

  const send = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error(isAr ? 'العنوان والمحتوى مطلوبان' : 'Title and body are required');
      return;
    }
    if (!window.confirm(
      isAr
        ? `سيصل الإعلان إلى: ${AUDIENCES.find((a) => a.value === audience)?.ar}. متابعة؟`
        : `Broadcast to: ${AUDIENCES.find((a) => a.value === audience)?.en}. Continue?`,
    )) return;

    setSending(true);
    setProgress(null);
    const activeChannels = Object.entries(channels).filter(([, v]) => v).map(([k]) => k);
    const res = await bridge.announcement.broadcast({ title, body, audience, channels: activeChannels });
    setSending(false);

    if (res.success) {
      const id = res.data?.job_id || res.data?.command_id;
      setJobId(id);
      toast.success(isAr ? '📤 بدء البثّ...' : '📤 Broadcast queued...');
      setTitle(''); setBody('');
    } else {
      toast.error(`⚠️ ${res.error?.message || 'Failed'}`);
    }
  };

  // Poll job progress every 3 seconds
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    const tick = async () => {
      const res = await bridge.commands.get(jobId);
      if (cancelled) return;
      if (res.success !== false) {
        const p = res.data?.progress || res.data;
        setProgress(p);
        if (p?.status === 'done' || p?.status === 'failed') {
          toast[p.status === 'done' ? 'success' : 'error'](
            p.status === 'done'
              ? (isAr ? '✅ اكتمل البثّ' : '✅ Broadcast completed')
              : (isAr ? '⚠️ فشل البثّ' : '⚠️ Broadcast failed'),
          );
        }
      }
    };
    tick();
    const int = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(int); };
  }, [jobId, isAr]);

  return (
    <div className="space-y-4 max-w-2xl" data-testid="f606-broadcast-admin">
      <div className="flex items-center gap-2">
        <Megaphone className="h-5 w-5 text-cyan-300" />
        <h3 className="text-white font-bold">{isAr ? 'بثّ إعلان جماعي' : 'Broadcast Announcement'}</h3>
      </div>

      <div>
        <label className="block text-[11px] text-white/50 mb-1">{isAr ? 'العنوان' : 'Title'}</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={100}
          className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm"
          data-testid="f606-broadcast-title"
        />
      </div>

      <div>
        <label className="block text-[11px] text-white/50 mb-1">{isAr ? 'المحتوى' : 'Body'}</label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          maxLength={500}
          className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm resize-none"
          data-testid="f606-broadcast-body"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] text-white/50 mb-1">{isAr ? 'الجمهور' : 'Audience'}</label>
          <select
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm"
            data-testid="f606-broadcast-audience"
          >
            {AUDIENCES.map((a) => <option key={a.value} value={a.value}>{isAr ? a.ar : a.en}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] text-white/50 mb-1">{isAr ? 'القنوات' : 'Channels'}</label>
          <div className="flex flex-wrap gap-2">
            {['push', 'email', 'in_app', 'sms'].map((c) => (
              <label key={c} className="flex items-center gap-1 text-xs text-white/70 cursor-pointer">
                <input
                  type="checkbox"
                  checked={channels[c] || false}
                  onChange={(e) => setChannels((s) => ({ ...s, [c]: e.target.checked }))}
                  data-testid={`f606-broadcast-channel-${c}`}
                />
                {c === 'push' ? 'Push 🟡' : c === 'sms' ? 'SMS 🟡' : c.replace('_', ' ')}
              </label>
            ))}
          </div>
          <p className="text-[9px] text-amber-300/70 mt-1">
            {isAr ? '🟡 = Mocked (لم يُفعَّل الإرسال الفعلي بعد)' : '🟡 = Mocked (delivery not yet live)'}
          </p>
        </div>
      </div>

      <button
        onClick={send}
        disabled={sending || !title.trim() || !body.trim()}
        className="w-full py-3 rounded-xl bg-cyan-500/20 border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/30 font-bold flex items-center justify-center gap-2 disabled:opacity-40 transition"
        data-testid="f606-broadcast-send"
      >
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {isAr ? 'إرسال الآن' : 'Send now'}
      </button>

      {progress && (
        <div className="rounded-xl border border-white/10 bg-black/30 p-3" data-testid="f606-broadcast-progress">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="text-white/70">Job: <span className="font-mono text-cyan-300" dir="ltr">{jobId}</span></span>
            <span className="text-white/60">{progress.status || '—'}</span>
          </div>
          {progress.total > 0 && (
            <div className="h-2 rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full bg-cyan-400 transition-all"
                style={{ width: `${Math.min(100, (progress.done / progress.total) * 100)}%` }}
              />
            </div>
          )}
          <div className="text-[10px] text-white/50 mt-1 font-mono" dir="ltr">
            done: {progress.done ?? 0} / total: {progress.total ?? 0} · failed: {progress.failed ?? 0}
          </div>
        </div>
      )}
    </div>
  );
}
